import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, X, Plus, ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import { indexBranches, fullName, relabelBranch } from '../../lib/employee.js'
import { nameInitial } from '../../lib/text.js'
import { EmployeeTrigger } from '../../components/employee/EmployeeDrawer.jsx'
import { nowYM, leaveTypeMeta } from '../../lib/timetable.js'
import {
  leaveQuota, yearRange, fmtDMY, groupRowsByRound, fmtRoundLabel, normLeaveSummary,
} from '../../lib/leave.js'
import MonthPicker from '../../components/employee/MonthPicker.jsx'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'
import LeaveEditor from '../../components/employee/LeaveEditor.jsx'

const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const pad2 = (n) => String(n).padStart(2, '0')

// Employee identity cell — CODE is the primary line (per spec, across the whole
// page); the name is secondary. Avatar is a text icon (Thai initial), no photo.
// `employeeId` turns the cell into a second click target that opens that person's
// record (EmployeeDrawer.jsx) — the row's own click is already spoken for. Optional,
// so a caller that has no id still renders the same cell, just not clickable.
function EmpCell({ code, name, employeeId }) {
  const inner = (
    <>
      <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 text-[12.5px] font-semibold flex items-center justify-center shrink-0 ring-1 ring-brand-100">
        {nameInitial(name || code || '?')}
      </span>
      <div className="min-w-0">
        <div className="font-semibold text-slate-800 truncate tabular-nums">{code || '—'}</div>
        {name && <div className="text-[12px] text-slate-400 truncate">{name}</div>}
      </div>
    </>
  )
  if (!employeeId) return <div className="flex items-center gap-2.5 min-w-0">{inner}</div>
  return <EmployeeTrigger employeeId={employeeId} className="items-center gap-2.5">{inner}</EmployeeTrigger>
}

function MiniStat({ label, value, tone = 'text-slate-800' }) {
  return (
    <div>
      <div className="text-[11.5px] text-slate-400">{label}</div>
      <div className={`text-[15px] font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  )
}

// used / quota, tinted amber when nearly out and rose when over.
function QuotaCell({ q }) {
  return (
    <span className={`tabular-nums font-medium whitespace-nowrap ${q.over ? 'text-rose-600' : q.remaining <= 2 ? 'text-amber-600' : 'text-slate-700'}`}>
      {q.used} <span className="text-slate-400 font-normal">/ {q.quota}</span>
    </span>
  )
}

export default function EmployeeLeave() {
  const [branches, setBranches] = useState([])
  const [employees, setEmployees] = useState([])

  const [year, setYear] = usePersistedState('emp:lv:year', () => nowYM().year)
  const [branchId, setBranchId] = usePersistedState('emp:lv:branch', '')
  // Shared with every พนักงาน tab — see EmployeeFilter.jsx.
  const [employeeId, setEmployeeId] = useEmployeeFilter()

  // conclusion (grouped totals, server-aggregated) — the summary + quota table.
  const [summary, setSummary] = useState([])
  const [sumLoading, setSumLoading] = useState(true)
  const [sumErr, setSumErr] = useState('')
  const sumReq = useRef(0)

  // detail (rows behind the selected employee, grouped by รอบ)
  const [selected, setSelected] = useState(null) // employee_id | null
  const [detRows, setDetRows] = useState([])
  const [detLoading, setDetLoading] = useState(false)
  const [detErr, setDetErr] = useState('')
  const detReq = useRef(0)

  const [editing, setEditing] = useState(null)

  useEffect(() => {
    cachedGet('/api/branches')
      .then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  const branchName = useMemo(() => indexBranches(branches), [branches])
  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])

  const filters = useMemo(() => {
    const { start_date, end_date } = yearRange(year)
    const f = { start_date, end_date }
    if (branchId) f.branch_id = branchId
    if (employeeId) f.employee_id = employeeId
    return f
  }, [year, branchId, employeeId])
  const filterKey = JSON.stringify(filters)

  // conclusion: one grouped query for the whole year. Changing any filter clears
  // the open employee (resetSelection) — mirrors the ลงเวลา report. Filtered to ONE
  // person, the summary is a single row, so it opens that person's detail instead.
  function loadSummary(resetSelection) {
    const id = ++sumReq.current
    setSumLoading(true); setSumErr('')
    if (resetSelection) setSelected(employeeId ? Number(employeeId) : null)
    apiGet('/api/hr/leave-summary', filters)
      .then((r) => {
        if (id !== sumReq.current) return
        setSummary((Array.isArray(r) ? r : (r?.items || [])).map(normLeaveSummary))
      })
      .catch((e) => { if (id === sumReq.current) setSumErr(String(e.message || e)) })
      .finally(() => { if (id === sumReq.current) setSumLoading(false) })
  }
  useEffect(() => { loadSummary(true) }, [filterKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // detail: the selected employee's leave days for the year.
  function loadDetail(empId) {
    if (!empId) { setDetRows([]); setDetErr(''); return }
    const id = ++detReq.current
    setDetLoading(true); setDetErr('')
    apiGet('/api/hr/leave', { employee_id: empId, start_date: filters.start_date, end_date: filters.end_date, per_page: 200 })
      .then((r) => { if (id === detReq.current) setDetRows(Array.isArray(r) ? r : (r?.items || [])) })
      .catch((e) => { if (id === detReq.current) setDetErr(String(e.message || e)) })
      .finally(() => { if (id === detReq.current) setDetLoading(false) })
  }
  useEffect(() => { loadDetail(selected) }, [selected, filters.start_date, filters.end_date]) // eslint-disable-line react-hooks/exhaustive-deps

  const rounds = useMemo(() => groupRowsByRound(detRows), [detRows])

  // quota view-model per summary row (leaveQuota needs the employee's date_probationpass/sso).
  const quotaOf = (s) => {
    const emp = empById.get(s.employee_id) || { id: s.employee_id, sso: s.sso, code: s.code, branch_id: s.branch_id }
    return { emp, q: leaveQuota(emp, year, { personal: s.personal, sick: s.sick }) }
  }

  const selSummary = useMemo(() => summary.find((r) => r.employee_id === selected) || null, [summary, selected])
  const sel = selSummary ? quotaOf(selSummary) : null
  const selCode = selSummary?.code || empById.get(Number(selected))?.code || `#${selected}`
  const selName = selSummary?.name || (empById.get(Number(selected)) ? fullName(empById.get(Number(selected))) : '')

  const hasFilter = branchId || employeeId
  const defaultDate = useMemo(() => {
    const now = nowYM()
    if (Number(year) === now.year) {
      const d = new Date()
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
    }
    return `${year}-01-01`
  }, [year])

  const openAdd = (empId) => {
    const emp = empId != null ? empById.get(Number(empId)) : null
    setEditing({
      defaults: {
        employee_id: empId ?? (employeeId || ''),
        branch_id: emp?.branch_id ?? (branchId || ''),
        date: defaultDate,
      },
    })
  }
  const afterSave = () => { setEditing(null); loadSummary(false); loadDetail(selected) }

  return (
    <div className="max-w-[1180px]">
      <p className="text-[13.5px] text-slate-500 mb-4">
        สรุปการลาและโควตาของพนักงานรายปี (ลากิจ · ลาป่วย · Leave without pay ไม่นับโควตา) — คลิกแถวเพื่อดูวันลารายรอบของแต่ละคน
      </p>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <MonthPicker year={year} showMonth={false} era="CE" onChange={(y) => setYear(y)} />
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SELECT}>
          <option value="">ทุกสาขา</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
        </select>
        <EmployeePicker value={employeeId} onChange={setEmployeeId} allowAll />
        {hasFilter && (
          <button onClick={() => { setBranchId(''); setEmployeeId('') }}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
            <X size={14} /> ล้าง
          </button>
        )}
        <button onClick={() => openAdd(selected ?? null)}
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700">
          <Plus size={15} /> เพิ่มวันลา
        </button>
      </div>

      {sumLoading && (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-16 text-center text-slate-400">
          <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…
        </div>
      )}
      {!sumLoading && sumErr && (
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-16 text-center text-rose-500">{sumErr}</div>
      )}

      {/* ── สรุปการลา + โควตา (main) — collapses once an employee is picked ── */}
      {!sumLoading && !sumErr && !selected && (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13.5px]">
                <thead>
                  <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
                    <th className="px-4 py-2.5">พนักงาน</th>
                    <th className="px-4 py-2.5">สาขา</th>
                    <th className="px-4 py-2.5">ปกส.</th>
                    <th className="px-4 py-2.5 text-right">ลากิจ (ใช้/สิทธิ)</th>
                    <th className="px-4 py-2.5 text-right">ลาป่วย (ใช้/สิทธิ)</th>
                    <th className="px-4 py-2.5 text-right" title="ไม่นับในโควตาลากิจ · ไม่ได้รับเงินเดือนวันนั้น">Leave without pay</th>
                    <th className="px-4 py-2.5 text-right">ไม่แจ้ง</th>
                    <th className="px-4 py-2.5 text-right">รวมวันลา</th>
                    <th className="px-4 py-2.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {!summary.length && (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">ไม่พบวันลาในปีที่เลือก</td></tr>
                  )}
                  {summary.map((s) => {
                    const { emp, q } = quotaOf(s)
                    return (
                      <tr key={s.key} onClick={() => setSelected(s.employee_id)}
                        className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition">
                        {/* The row opens the person's leave rounds below; the
                            avatar+name opens the person. */}
                        <td className="px-4 py-3"><EmpCell code={s.code} name={s.name} employeeId={s.employee_id} /></td>
                        <td className="px-4 py-3 text-slate-500">{relabelBranch(branchName.get(Number(emp?.branch_id) || s.branch_id) || '')}</td>
                        <td className="px-4 py-3">
                          {emp?.sso
                            ? <span className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 bg-emerald-50 text-emerald-700 ring-emerald-200">ผู้ประกันตน</span>
                            : <span className="text-[12px] text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right"><QuotaCell q={q.personal} /></td>
                        <td className="px-4 py-3 text-right"><QuotaCell q={q.sick} /></td>
                        <td className="px-4 py-3 text-right tabular-nums">{s.unpaid ? <span className="text-stone-700">{s.unpaid}</span> : <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{s.unnotice ? <span className="text-rose-600">{s.unnotice}</span> : <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-800">{s.total}</td>
                        <td className="px-4 py-3 text-right"><ChevronRight size={15} className="text-slate-300" /></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {summary.length > 0 && (
            <p className="text-[12.5px] text-slate-400 mt-3">คลิกแถวพนักงานเพื่อดูวันลารายรอบ (26 → 25)</p>
          )}
        </>
      )}

      {/* ── detail: the selected employee's leave days, grouped by รอบ ── */}
      {!sumLoading && !sumErr && selected && (
        <>
          <div className="flex items-center justify-between gap-4 mb-3 text-[12.5px]">
            {/* Filtered to this person, "back" means everyone — the one-row summary
                behind it is not a place anyone wants to land. */}
            <button onClick={() => (employeeId ? setEmployeeId('') : setSelected(null))}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 -ml-1 rounded-lg font-medium text-slate-600 hover:bg-slate-100">
              <ChevronLeft size={15} /> {employeeId ? 'ดูพนักงานทุกคน' : 'กลับไปตารางสรุป'}
            </button>
            <div className="flex items-center gap-1.5 text-slate-400 min-w-0">
              <ClipboardList size={14} className="shrink-0" />
              <span className="truncate">กำลังดู</span>
              <span className="font-semibold text-brand-700 tabular-nums truncate">{selCode}</span>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden mb-4">
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <EmpCell code={selCode} name={selName} employeeId={selected} />
                <button onClick={() => openAdd(selected)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-brand-700 ring-1 ring-brand-200 bg-brand-50 hover:bg-brand-100">
                  <Plus size={14} /> เพิ่มวันลา
                </button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 mt-4 rounded-xl bg-slate-50 px-4 py-3">
                <MiniStat label="ลากิจ (ใช้/สิทธิ)" value={sel ? `${sel.q.personal.used}/${sel.q.personal.quota}` : '—'} tone={sel?.q.personal.over ? 'text-rose-600' : 'text-amber-600'} />
                <MiniStat label="ลาป่วย (ใช้/สิทธิ)" value={sel ? `${sel.q.sick.used}/${sel.q.sick.quota}` : '—'} tone={sel?.q.sick.over ? 'text-rose-600' : 'text-rose-600'} />
                <MiniStat label="Leave without pay" value={(selSummary?.unpaid ?? 0).toLocaleString()} tone="text-stone-700" />
                <MiniStat label="รวมวันลา" value={(selSummary?.total ?? 0).toLocaleString()} />
                <MiniStat label="ไม่แจ้งล่วงหน้า" value={(selSummary?.unnotice ?? 0).toLocaleString()} tone={selSummary?.unnotice ? 'text-rose-600' : 'text-slate-800'} />
                <MiniStat label="มีใบรับรองแพทย์" value={(selSummary?.med_certificate ?? 0).toLocaleString()} tone="text-emerald-600" />
              </div>
            </div>
          </div>

          {detLoading ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</div>
          ) : detErr ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center text-rose-500">{detErr}</div>
          ) : !rounds.length ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-400">ไม่มีวันลาในปีนี้</div>
          ) : (
            <div className="space-y-3">
              {rounds.map((g) => (
                <div key={g.round.key} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/50">
                    <span className="text-[13px] font-semibold text-slate-700 whitespace-nowrap">{fmtRoundLabel(g.round)}</span>
                    <span className="ml-auto text-[12px] text-slate-400 tabular-nums">{g.rows.length} วัน</span>
                  </div>
                  <table className="w-full text-[13.5px]">
                    <tbody>
                      {g.rows.map((r) => {
                        const meta = leaveTypeMeta(r.type)
                        return (
                          <tr key={r.id} onClick={() => setEditing({ row: r })}
                            className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition">
                            <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap tabular-nums w-[110px]">{fmtDMY(r.date)}</td>
                            <td className="px-4 py-2.5 w-[92px]">
                              <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${meta.tone}`}>{meta.label}</span>
                            </td>
                            <td className="px-4 py-2.5 w-[86px]">
                              {r.unnotice
                                ? <span className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 bg-rose-50 text-rose-700 ring-rose-200">ไม่แจ้ง</span>
                                : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-4 py-2.5 w-[104px]">
                              {r.med_certificate
                                ? <span className="inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 bg-emerald-50 text-emerald-700 ring-emerald-200">มีใบรับรอง</span>
                                : <span className="text-slate-300">—</span>}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500 truncate">{r.remark || '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <LeaveEditor
          row={editing.row || null}
          defaults={editing.defaults || null}
          branches={branches}
          employees={employees}
          onClose={() => setEditing(null)}
          onSaved={afterSave}
        />
      )}
    </div>
  )
}
